import { useLocation } from "@remix-run/react";
import { useLayoutEffect, useRef } from "react";

/**
 * Context-aware hash navigation:
 *
 * - ARRIVING from another page (/contact → "/#cursos"): the user should simply
 *   APPEAR at the section — instant, before paint, no hero flash. The async
 *   sections render skeletons with their final ids from first paint, and we keep
 *   the target pinned (still instant) while content above streams in.
 * - ALREADY on the home and clicking an anchor: a SMOOTH scroll with natural
 *   ease-in/out — the browser's smooth behavior — feels intentional and alive.
 *
 * We tell the two apart with a mount ref: the first effect run after the Home
 * mounts = arrival; every later run (hash/key change while mounted) = in-page click.
 */
export function useHashScroll() {
  const { hash, key } = useLocation();
  const hasMountedRef = useRef(false);

  useLayoutEffect(() => {
    const isInPageClick = hasMountedRef.current;
    hasMountedRef.current = true;
    if (!hash) return;
    const id = hash.slice(1);
    let cancelled = false;

    const align = (behavior: ScrollBehavior) => {
      const el = document.getElementById(id);
      if (!el) return false;
      el.scrollIntoView({ behavior, block: "start" });
      return true;
    };

    if (isInPageClick) {
      // Smooth glide (with acceleration/deceleration) — content is already loaded.
      align("smooth");
      return;
    }

    // Arrival: pin before first paint…
    align("instant" as ScrollBehavior);

    // …and keep it pinned while async content above settles (1.6s window),
    // unless the user takes over the scroll.
    let userScrolled = false;
    const onUserScroll = () => {
      userScrolled = true;
    };
    window.addEventListener("wheel", onUserScroll, { passive: true });
    window.addEventListener("touchmove", onUserScroll, { passive: true });

    const started = performance.now();
    const tick = () => {
      if (cancelled || userScrolled) return;
      align("instant" as ScrollBehavior);
      if (performance.now() - started < 1600) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchmove", onUserScroll);
    };
    // `key` re-fires when the same hash link is clicked again (new history entry).
  }, [hash, key]);
}
